import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Form, Input, Select, Space, Spin, Switch, Tabs, Typography, message } from "antd";
import { LeftOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useSmartBack } from "../../utils/useSmartBack";
import PageLogoutButton from "../../Components/PageLogoutButton";
import NavigationDropdown from "../../Components/NavigationDropDown";
import {
  fetchOpenOrderReport,
  fetchOrderTrackingCenterReport,
  getExternalReportSettings,
  saveExternalReportSettings,
  validateExternalReportToken,
  type ExternalReportSettings,
} from "../../utils/externalReports";
import {
  getAppSettings,
  updateAppToken,
  updateAppSettings,
  getCronSettings,
  saveCronSettings,
  getEmailTemplates,
  type CronFollowUpSetting,
} from "../../utils/API";

const { Paragraph, Text, Title } = Typography;

type FollowUpCron = CronFollowUpSetting;

const defaultFollowUp = (): FollowUpCron => ({
  enabled: false,
  daysAfter: null,
  runTime: "06:00",
  templateSlug: null,
  skipWeekends: true,
});

const FollowUpCard: React.FC<{
  label: string;
  hint: string;
  badgeColor: string;
  value: FollowUpCron;
  templateOptions: { value: string; label: string }[];
  onChange: (v: FollowUpCron) => void;
}> = ({ label, hint, badgeColor, value, templateOptions, onChange }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex-1">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100" style={{ backgroundColor: `${badgeColor}08` }}>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: badgeColor }} />
        <span className="text-sm font-semibold text-gray-800">{label}</span>
      </div>
      <Switch
        checked={value.enabled}
        onChange={(checked) => onChange({ ...value, enabled: checked })}
        style={{ backgroundColor: value.enabled ? badgeColor : "#9ca3af" }}
      />
    </div>
    <div className={`flex flex-col gap-4 px-4 py-4 ${!value.enabled ? "opacity-40 pointer-events-none" : ""}`}>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Days</label>
        <Input
          type="number"
          min={1}
          max={365}
          value={value.daysAfter ?? ""}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange({ ...value, daysAfter: Number.isFinite(n) ? n : null });
          }}
          placeholder="e.g. 3"
          disabled={!value.enabled}
          suffix={<span className="text-gray-400 text-sm select-none">days</span>}
          style={{ height: 40, fontSize: 14 }}
        />
        <span className="text-xs text-gray-400 mt-0.5 block">{hint}</span>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Email Template</label>
        <Select
          value={value.templateSlug || undefined}
          onChange={(v) => onChange({ ...value, templateSlug: v || null })}
          placeholder="Select template"
          className="w-full cron-days-input"
          allowClear
          disabled={!value.enabled}
          options={templateOptions}
          loading={templateOptions.length === 0}
          style={{ width: "100%" }}
        />
      </div>
    </div>
  </div>
);

const SettingsPage: React.FC = () => {
  const smartBack = useSmartBack();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "settings";

  const [firstFollowUp, setFirstFollowUp] = useState<FollowUpCron>(defaultFollowUp());
  const [secondFollowUp, setSecondFollowUp] = useState<FollowUpCron>(defaultFollowUp());
  const [finalFollowUp, setFinalFollowUp] = useState<FollowUpCron>(defaultFollowUp());
  const [templateOptions, setTemplateOptions] = useState<{ value: string; label: string }[]>([]);
  const [cronLoading, setCronLoading] = useState(false);
  const [cronSaving, setCronSaving] = useState(false);
  const [form] = Form.useForm<ExternalReportSettings>();
  const [loadingTarget, setLoadingTarget] = useState<string>("");
  const [initializing, setInitializing] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load token from backend on mount; fall back to localStorage for URLs
  useEffect(() => {
    const load = async () => {
      try {
        const [backendSetting, localSettings] = await Promise.all([
          getAppSettings(),
          Promise.resolve(getExternalReportSettings()),
        ]);

        // Sender email accounts: prefer the saved list; fall back to the legacy
        // single support email/password; otherwise start with one empty row.
        const savedAccounts = Array.isArray(backendSetting?.emailAccounts)
          ? backendSetting!.emailAccounts!.filter((a) => a && String(a.email || "").trim())
          : [];
        const emailAccounts =
          savedAccounts.length > 0
            ? savedAccounts
            : backendSetting?.supportEmail
              ? [{ email: backendSetting.supportEmail, password: backendSetting.supportPassword || "" }]
              : [{ email: "", password: "" }];

        form.setFieldsValue({
          accessToken: backendSetting?.accessToken || localSettings.accessToken || "",
          formToken: backendSetting?.formToken || "",
          adsToken: backendSetting?.adsToken || "",
          chatgptToken: backendSetting?.chatgptToken || "",
          orderTrackingCenterUrl: localSettings.orderTrackingCenterUrl || "",
          openOrderReportUrl: localSettings.openOrderReportUrl || "",
          emailAccounts,
        } as any);

        // Sync backend token into localStorage so externalReports helpers work
        if (backendSetting?.accessToken) {
          saveExternalReportSettings({
            ...localSettings,
            accessToken: backendSetting.accessToken,
          });
        }
      } catch {
        // Backend unreachable — fall back to localStorage
        form.setFieldsValue(getExternalReportSettings());
      } finally {
        setInitializing(false);
      }
    };

    load();
  }, [form]);

  // Load cron settings + email templates when Cron tab is opened
  useEffect(() => {
    if (activeTab !== "cron") return;
    const loadCron = async () => {
      setCronLoading(true);
      try {
        const [settings, templates] = await Promise.all([
          getCronSettings(),
          getEmailTemplates(),
        ]);
        const toFollowUp = (s: any): FollowUpCron => ({
          enabled:      Boolean(s?.enabled),
          daysAfter:    s?.daysAfter ?? null,
          runTime:      s?.runTime || "09:00",
          templateSlug: s?.templateSlug || null,
          skipWeekends: Boolean(s?.skipWeekends),
        });
        setFirstFollowUp(toFollowUp(settings.first));
        setSecondFollowUp(toFollowUp(settings.second));
        setFinalFollowUp(toFollowUp(settings.final));
        setTemplateOptions(
          (Array.isArray(templates) ? templates : []).map((t: any) => ({
            value: t.slug || t.name,
            label: t.name,
          })),
        );
      } catch {
        message.error("Failed to load cron settings");
      } finally {
        setCronLoading(false);
      }
    };
    loadCron();
  }, [activeTab]);

  const handleSave = async (values: ExternalReportSettings & { formToken?: string; adsToken?: string; chatgptToken?: string; emailAccounts?: { email?: string; password?: string }[] }) => {
    setSaving(true);
    try {
      const emailAccounts = (values.emailAccounts || [])
        .map((a) => ({ email: String(a?.email || "").trim(), password: String(a?.password || "").trim() }))
        .filter((a) => a.email);
      await updateAppSettings({
        accessToken: values.accessToken,
        formToken: values.formToken || "",
        adsToken: values.adsToken || "",
        chatgptToken: values.chatgptToken || "",
        emailAccounts,
      });
      saveExternalReportSettings(values);
      message.success("Settings saved.");
    } catch {
      message.error("Failed to save settings to server. Local settings saved.");
      saveExternalReportSettings(values);
    } finally {
      setSaving(false);
    }
  };

  const handleTokenValidation = async () => {
    const token = form.getFieldValue("accessToken");
    const result = validateExternalReportToken(token);

    if (!result.isValid) {
      message.error(result.message);
      return;
    }

    message.success(result.message);
  };

  const withSavedSettings = (): ExternalReportSettings => {
    const values = form.getFieldsValue();
    const saved = saveExternalReportSettings({
      accessToken: values.accessToken || "",
      orderTrackingCenterUrl: values.orderTrackingCenterUrl || "",
      openOrderReportUrl: values.openOrderReportUrl || "",
    });
    return saved;
  };

  const handleSaveCron = async () => {
    setCronSaving(true);
    try {
      const forceSkip = (f: FollowUpCron) => ({ ...f, skipWeekends: true, runTime: "06:00" });
      await saveCronSettings({ first: forceSkip(firstFollowUp), second: forceSkip(secondFollowUp), final: forceSkip(finalFollowUp) });
      message.success("Cron settings saved.");
    } catch {
      message.error("Failed to save cron settings.");
    } finally {
      setCronSaving(false);
    }
  };

  const handleTest = async (target: "orderTrackingCenter" | "openOrderReport") => {
    try {
      setLoadingTarget(target);
      withSavedSettings();

      const rows =
        target === "orderTrackingCenter"
          ? await fetchOrderTrackingCenterReport()
          : await fetchOpenOrderReport();

      message.success(
        `${rows.length} row(s) received. Only model, date, sale price, cost and GM are mapped.`,
      );
    } catch (error: any) {
      message.error(error?.message || "Unable to test external report API.");
    } finally {
      setLoadingTarget("");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Button
            icon={<LeftOutlined />}
            size="small"
            className="flex items-center justify-center rounded-md border-gray-200"
            onClick={smartBack}
          />
          <NavigationDropdown />
          <Title level={4} className="!mb-0">Settings</Title>
        </div>
        <PageLogoutButton />
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
          tabBarExtraContent={
            activeTab === "cron" ? (
              <Button type="primary" size="small" loading={cronSaving} className="bg-[#004aad]" onClick={handleSaveCron}>
                Save Cron Settings
              </Button>
            ) : null
          }
          items={[
            {
              key: "settings",
              label: "Settings",
              children: (
                <Spin spinning={initializing}>
                  <Form form={form} layout="vertical" onFinish={handleSave}>
                    <div className="grid grid-cols-2 gap-4 mb-6">

                      {/* API Access Section */}
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-1 h-5 bg-[#004aad] rounded-full" />
                          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">API Access</span>
                        </div>
                        <Form.Item
                          label={<span className="text-sm font-medium text-gray-600">External Token</span>}
                          name="accessToken"
                          rules={[{ required: true, message: "Please enter the external token." }]}
                          className="mb-4"
                        >
                          <Input.Password
                            size="large"
                            placeholder="Paste the token used for external report validation"
                            className="rounded-lg"
                          />
                        </Form.Item>
                        <Form.Item
                          label={<span className="text-sm font-medium text-gray-600">Form Token</span>}
                          name="formToken"
                          className="mb-4"
                        >
                          <Input.Password
                            size="large"
                            placeholder="Paste the form token"
                            className="rounded-lg"
                          />
                        </Form.Item>
                        <Form.Item
                          label={<span className="text-sm font-medium text-gray-600">Ads Token</span>}
                          name="adsToken"
                          className="mb-4"
                        >
                          <Input.Password
                            size="large"
                            placeholder="Paste the ads API token"
                            className="rounded-lg"
                          />
                        </Form.Item>
                        <Form.Item
                          label={<span className="text-sm font-medium text-gray-600">ChatGPT Token</span>}
                          name="chatgptToken"
                          className="mb-0"
                        >
                          <Input.Password
                            size="large"
                            placeholder="Paste the ChatGPT API token"
                            className="rounded-lg"
                          />
                        </Form.Item>
                      </div>

                      {/* Email Credentials Section */}
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-1 h-5 bg-[#f97316] rounded-full" />
                          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Email Credentials</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">
                          All outgoing mail is sent from these accounts. The first one is the default sender.
                        </p>
                        <Form.List name="emailAccounts">
                          {(fields, { add, remove }) => (
                            <div className="flex flex-col gap-4">
                              {fields.map((field, index) => (
                                <div
                                  key={field.key}
                                  className="rounded-lg border border-gray-100 bg-gray-50/60 p-3"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                      {index === 0 ? "Default Account" : `Account ${index + 1}`}
                                    </span>
                                    {fields.length > 1 && (
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => remove(field.name)}
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </div>
                                  <Form.Item
                                    {...field}
                                    key={`${field.key}-email`}
                                    label={<span className="text-sm font-medium text-gray-600">Support Email</span>}
                                    name={[field.name, "email"]}
                                    rules={[
                                      { required: true, message: "Please enter the email address." },
                                      { type: "email", message: "Enter a valid email address." },
                                    ]}
                                    className="mb-3"
                                  >
                                    <Input
                                      size="large"
                                      placeholder="e.g. sanderson@alliancetesteq.com"
                                      className="rounded-lg"
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...field}
                                    key={`${field.key}-password`}
                                    label={<span className="text-sm font-medium text-gray-600">Support Password</span>}
                                    name={[field.name, "password"]}
                                    className="mb-0"
                                  >
                                    <Input.Password
                                      size="large"
                                      placeholder="App password for the support email account"
                                      className="rounded-lg"
                                    />
                                  </Form.Item>
                                </div>
                              ))}
                              <Button
                                type="dashed"
                                onClick={() => add({ email: "", password: "" })}
                                icon={<PlusOutlined />}
                                className="w-full"
                              >
                                Add Email
                              </Button>
                            </div>
                          )}
                        </Form.List>
                      </div>

                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={saving}
                        size="large"
                        className="bg-[#004aad] rounded-lg px-8 h-10 font-medium shadow-sm"
                      >
                        Save Settings
                      </Button>
                    </div>
                  </Form>
                </Spin>
              ),
            },
            {
              key: "cron",
              label: "Manage Cron",
              children: (
                <Spin spinning={cronLoading}>
                  <div className="flex gap-4">
                    <FollowUpCard label="1st Follow Up" hint="Days after FQ sent" badgeColor="#004aad" value={firstFollowUp} templateOptions={templateOptions} onChange={setFirstFollowUp} />
                    <FollowUpCard label="2nd Follow Up" hint="Days after 1st follow up" badgeColor="#f97316" value={secondFollowUp} templateOptions={templateOptions} onChange={setSecondFollowUp} />
                    <FollowUpCard label="Final Follow Up" hint="Days after 2nd follow up" badgeColor="#ef4444" value={finalFollowUp} templateOptions={templateOptions} onChange={setFinalFollowUp} />
                  </div>
                </Spin>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default SettingsPage;
